import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, Users, UserPlus, Mail, Clock, Trash2, Copy, CheckCircle2, AlertCircle, AlertTriangle, X, User } from 'lucide-react';
import EmailInviteModal from './EmailInviteModal';
import { errorMessage, reportError } from '../../lib/errors';

interface Member {
    id: string;
    user_id: string;
    role: string;
    created_at: string;
    profiles: { full_name: string | null; email: string | null } | null;
}

interface Invite {
    id: string;
    email: string | null;
    role: string;
    status: string;
    token: string;
    created_at: string;
    expires_at: string | null;
}

interface DeleteTarget {
    memberId: string;
    memberUserId: string;
    memberName: string;
}

interface PendingInviteData {
    email: string;
    name: string;
    token: string;
}

export const TeamManagementSection = () => {
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState<Member[]>([]);
    const [invites, setInvites] = useState<Invite[]>([]);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [orgName, setOrgName] = useState<string>('');
    const [inviterName, setInviterName] = useState<string>('');
    const [maxMembers, setMaxMembers] = useState<number>(1);
    const [tier, setTier] = useState<string>('free');

    // Invite form
    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('mechanic');
    const [inviting, setInviting] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
    const [copiedToken, setCopiedToken] = useState<string | null>(null);

    // Email modal
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [pendingInvite, setPendingInvite] = useState<PendingInviteData | null>(null);

    // Danger delete modal
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    const fetchData = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('active_org_id, full_name')
            .eq('id', user.id)
            .single();
        if (profileError) reportError(profileError, 'team-management-section.profiles');

        if (!profile?.active_org_id) return;
        setOrgId(profile.active_org_id);
        setInviterName(profile.full_name || user.email?.split('@')[0] || 'Your teammate');

        // Get org details
        const { data: orgData, error: orgDataError } = await supabase
            .from('organizations')
            .select('max_members, name, subscription_tier')
            .eq('id', profile.active_org_id)
            .single();
        if (orgDataError) reportError(orgDataError, 'team-management-section.organizations');

        if (orgData) {
            setMaxMembers(orgData.max_members || 1);
            setOrgName(orgData.name || 'the workshop');
            setTier(orgData.subscription_tier || 'free');
        }

        // The seat limit the database enforces: 1 on Free, otherwise the plan's
        // allowance plus any extra seats bought through support (20261013220000).
        const { data: limit, error: limitError } = await supabase
            .rpc('fn_org_seat_limit', { p_org_id: profile.active_org_id });
        if (limitError) reportError(limitError, 'team-management-section.fn_org_seat_limit');
        else if (typeof limit === 'number') setMaxMembers(limit);

        // Members with profile info
        const { data: memberData, error: memberDataError } = await supabase
            .from('organization_members')
            .select('id, user_id, role, created_at, profiles(full_name, email)')
            .eq('org_id', profile.active_org_id)
            .order('created_at', { ascending: true });
        if (memberDataError) reportError(memberDataError, 'team-management-section.organization-members');

        if (memberData) setMembers(memberData as unknown as Member[]);

        // Pending invites
        const { data: inviteData, error: inviteDataError } = await supabase
            .from('organization_invites')
            .select('id, email, role, status, token, created_at, expires_at')
            .eq('org_id', profile.active_org_id)
            .in('status', ['pending'])
            .order('created_at', { ascending: false });
        if (inviteDataError) reportError(inviteDataError, 'team-management-section.organization-invites');

        if (inviteData) setInvites(inviteData);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Seats in use: members (owner included) plus pending invites that have not
    // expired, which is exactly what fn_org_seats_used counts.
    const pendingInvites = invites.filter((i) => !i.expires_at || new Date(i.expires_at) > new Date());
    const seatsUsed = members.length + pendingInvites.length;
    const seatLimitMessage = () =>
        tier === 'free'
            ? 'The Free plan is just you. Upgrade to Pro to add a mechanic or your accountant.'
            : `All ${maxMembers} team seats are in use, pending invites included. Cancel a pending invite or remove a member — or contact support to buy extra seats.`;

    const handleInvite = async () => {
        if (!orgId || !inviteEmail.trim() || !inviteName.trim()) return;
        setInviting(true);
        setMsg(null);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // A pending invite holds its seat, so it counts. The database
            // enforces the same rule; this only saves a round trip.
            if (seatsUsed >= maxMembers) {
                setMsg({ type: 'error', text: seatLimitMessage() });
                setInviting(false);
                return;
            }

            const { data: inviteRecord, error } = await supabase
                .from('organization_invites')
                .insert({
                    org_id: orgId,
                    inviter_id: user.id,
                    email: inviteEmail.trim().toLowerCase(),
                    role: inviteRole,
                })
                .select('token')
                .single();

            if (error) throw error;

            // Store data for the email modal and open it
            setPendingInvite({
                email: inviteEmail.trim().toLowerCase(),
                name: inviteName.trim(),
                token: inviteRecord.token,
            });
            setEmailModalOpen(true);

            // Refresh the invites list
            await fetchData();
        } catch (err) {
            const text = errorMessage(err) || '';
            setMsg({ type: 'error', text: text.includes('seat_limit') ? seatLimitMessage() : text || 'Failed to create invite' });
        } finally {
            setInviting(false);
        }
    };

    const handleEmailModalSuccess = () => {
        setEmailModalOpen(false);
        setPendingInvite(null);
        setMsg({ type: 'success', text: `Invitation email sent to ${pendingInvite?.name || pendingInvite?.email}!` });
        setInviteName('');
        setInviteEmail('');
    };

    const handleEmailModalClose = () => {
        setEmailModalOpen(false);
        // The invite row exists but NO email went out. Saying "success" here is
        // what made the flow feel broken: a green tick, a row under Pending
        // Invites, and nothing in the invitee's inbox. Say so plainly instead.
        setMsg({
            type: 'warning',
            text: `Invite created for ${pendingInvite?.email}, but no email was sent. Copy the invite link below and share it manually, or revoke it and try again.`,
        });
        setPendingInvite(null);
        setInviteName('');
        setInviteEmail('');
    };

    const handleRevokeInvite = async (inviteId: string) => {
        const { error } = await supabase
            .from('organization_invites')
            .update({ status: 'revoked' })
            .eq('id', inviteId);

        if (!error) await fetchData();
    };

    const openDeleteModal = async (memberId: string, memberUserId: string, memberName: string) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || memberUserId === user.id) {
            setMsg({ type: 'error', text: 'You cannot remove yourself from the team.' });
            return;
        }

        setDeleteTarget({ memberId, memberUserId, memberName });
        setDeleteConfirmText('');
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);

        try {
            const { error } = await supabase
                .from('organization_members')
                .delete()
                .eq('id', deleteTarget.memberId);

            if (error) throw error;

            setDeleteTarget(null);
            setDeleteConfirmText('');
            setMsg({ type: 'success', text: `${deleteTarget.memberName} has been removed.` });
            await fetchData();
        } catch (err) {
            setMsg({ type: 'error', text: errorMessage(err) || 'Failed to remove member' });
        } finally {
            setDeleting(false);
        }
    };

    const closeDeleteModal = () => {
        setDeleteTarget(null);
        setDeleteConfirmText('');
    };

    const copyInviteLink = (token: string) => {
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/?invite_token=${token}`;
        navigator.clipboard.writeText(link);
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
    };

    const roleLabel = (role: string) => {
        const labels: Record<string, string> = {
            owner: 'Owner',
            admin: 'Accountant',
            mechanic: 'Mechanic',
            member: 'Member'
        };
        return labels[role.toLowerCase()] || role;
    };

    const roleBadgeColor = (role: string) => {
        const colors: Record<string, string> = {
            owner: 'bg-gold-fill/10 text-gold border-gold/20',
            admin: 'bg-info/10 text-info border-info/20',
            mechanic: 'bg-success/10 text-success border-success/20',
            member: 'bg-tint/5 text-fg-3 border-line-glow/20',
        };
        return colors[role.toLowerCase()] || colors.member;
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    const nameMatches = deleteTarget
        ? deleteConfirmText.trim().toLowerCase() === deleteTarget.memberName.trim().toLowerCase()
        : false;

    return (
        <>
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div>
                    <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                        <Users className="w-5 h-5 text-accent" />
                        Team Management
                    </h3>
                    <p className="text-sm text-fg-2">
                        {seatsUsed} / {maxMembers} seats used
                        {pendingInvites.length > 0 && ` · ${pendingInvites.length} pending invite${pendingInvites.length === 1 ? '' : 's'}`}
                    </p>
                </div>

                {/* Message */}
                {msg && (
                    <div className={`p-3 rounded-[var(--fds-radius)] flex items-center gap-3 text-sm ${msg.type === 'success'
                        ? 'bg-success/10 text-success border border-success/20'
                        : msg.type === 'warning'
                            ? 'bg-gold-fill/10 text-gold border border-gold/20'
                            : 'bg-danger/10 text-danger border border-danger/20'}`}>
                        {msg.type === 'success'
                            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                            : msg.type === 'warning'
                                ? <AlertTriangle className="w-4 h-4 shrink-0" />
                                : <AlertCircle className="w-4 h-4 shrink-0" />}
                        {msg.text}
                    </div>
                )}

                {/* Current Members */}
                <div className="space-y-3">
                    <p className="text-xs font-medium text-fg-2 uppercase tracking-wider">Active Members</p>
                    <div className="space-y-2">
                        {members.map((m) => (
                            <div key={m.id} className="flex items-center justify-between p-3 bg-raised border border-line rounded-[var(--fds-radius)]">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-full bg-raised flex items-center justify-center text-sm font-bold text-fg-2">
                                        {(m.profiles?.full_name || m.profiles?.email || '?')[0].toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-fg truncate">
                                            {m.profiles?.full_name || 'Unnamed'}
                                        </p>
                                        <p className="text-xs text-fg-2 truncate">
                                            {m.profiles?.email || ''}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${roleBadgeColor(m.role)}`}>
                                        {roleLabel(m.role)}
                                    </span>
                                    {m.role.toLowerCase() !== 'owner' && (
                                        <button
                                            onClick={() => openDeleteModal(m.id, m.user_id, m.profiles?.full_name || m.profiles?.email || 'Unnamed')}
                                            className="p-1.5 rounded hover:bg-danger/10 text-fg-2 hover:text-danger transition-colors"
                                            title="Remove member"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pending Invites */}
                {invites.length > 0 && (
                    <div className="space-y-3">
                        <p className="text-xs font-medium text-fg-2 uppercase tracking-wider">Pending Invites</p>
                        <div className="space-y-2">
                            {invites.map((inv) => (
                                <div key={inv.id} className="flex items-center justify-between p-3 bg-raised border border-gold/20 rounded-[var(--fds-radius)]">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-9 h-9 rounded-full bg-gold-fill/10 flex items-center justify-center">
                                            <Clock className="w-4 h-4 text-gold" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-fg truncate">
                                                {inv.email || 'Link-only invite'}
                                            </p>
                                            <p className="text-xs text-fg-2">
                                                Role: {roleLabel(inv.role)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => copyInviteLink(inv.token)}
                                            className="p-1.5 rounded hover:bg-raised text-fg-2 hover:text-fg transition-colors"
                                            title="Copy invite link"
                                        >
                                            {copiedToken === inv.token ? <CheckCircle2 className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => handleRevokeInvite(inv.id)}
                                            className="p-1.5 rounded hover:bg-danger/10 text-fg-2 hover:text-danger transition-colors"
                                            title="Revoke invite"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Invite Form */}
                <div className="border-t border-line pt-6 space-y-4">
                    <p className="text-sm font-medium text-fg flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-accent" />
                        Invite New Member
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Full Name */}
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-2 opacity-50" />
                            <input
                                type="text"
                                value={inviteName}
                                onChange={(e) => setInviteName(e.target.value)}
                                placeholder="Full name"
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] pl-10 pr-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                            />
                        </div>
                        {/* Email */}
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-2 opacity-50" />
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="team@example.com"
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] pl-10 pr-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all placeholder:text-fg-2/30"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <select
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value)}
                            className="bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-accent/50 transition-all"
                            style={{ colorScheme: 'dark' }}
                        >
                            <option value="mechanic" className="bg-card text-fg">Mechanic</option>
                            <option value="admin" className="bg-card text-fg">Accountant</option>
                        </select>
                        <button
                            onClick={handleInvite}
                            disabled={inviting || !inviteEmail.trim() || !inviteName.trim()}
                            className="flex items-center justify-center gap-2 fd-btn-primary px-5 py-2.5 rounded-[var(--fds-radius)] font-medium transition-opacity disabled:opacity-50 whitespace-nowrap"
                        >
                            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Send Invite
                        </button>
                    </div>
                </div>
            </div>

            {/* Danger Delete Modal */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-scrim/70 backdrop-blur-sm"
                        onClick={closeDeleteModal}
                    />

                    {/* Modal */}
                    <div className="relative z-10 w-full max-w-md mx-4 bg-card border border-danger/30 rounded-2xl shadow-2xl shadow-danger/10 animate-in fade-in zoom-in-95 duration-200">
                        {/* Close Button */}
                        <button
                            onClick={closeDeleteModal}
                            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-raised text-fg-2 hover:text-fg transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Warning Header */}
                        <div className="p-6 pb-0">
                            <div className="w-12 h-12 rounded-full bg-danger/15 flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle className="w-6 h-6 text-danger" />
                            </div>
                            <h4 className="text-lg font-bold text-fg text-center">
                                Delete Team Member
                            </h4>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <div className="bg-danger/8 border border-danger/20 rounded-[var(--fds-radius)] p-3">
                                <p className="text-sm text-danger leading-relaxed">
                                    This is irreversible. <strong className="text-danger">{deleteTarget.memberName}</strong> will be permanently deleted from the database and will not have access to ForgeDash.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm text-fg-2">
                                    Type <strong className="text-fg">{deleteTarget.memberName}</strong> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder={deleteTarget.memberName}
                                    className="w-full bg-raised border border-danger/30 rounded-[var(--fds-radius)] px-4 py-2.5 text-fg focus:outline-none focus:ring-2 focus:ring-danger/50 transition-all placeholder:text-fg-2/30"
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={closeDeleteModal}
                                    className="flex-1 px-4 py-2.5 rounded-[var(--fds-radius)] border border-line text-fg-2 font-medium hover:bg-raised transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={!nameMatches || deleting}
                                    className="flex-1 px-4 py-2.5 rounded-[var(--fds-radius)] bg-danger-fill text-on-fill font-medium hover:bg-danger-fill transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    Delete Permanently
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Invite Modal */}
            {pendingInvite && (
                <EmailInviteModal
                    isOpen={emailModalOpen}
                    onClose={handleEmailModalClose}
                    inviteeEmail={pendingInvite.email}
                    inviteeName={pendingInvite.name}
                    inviteToken={pendingInvite.token}
                    orgId={orgId || ''}
                    orgName={orgName}
                    inviterName={inviterName}
                    onSuccess={handleEmailModalSuccess}
                />
            )}
        </>
    );
};
